import type * as type from "@vTypes";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { cn } from "@/utils/cn";
import { Switch } from "@/components/ui/switch";
import { RxHamburgerMenu } from "react-icons/rx";
import Modal_shiftCodeList_perType from "../roster_configure/Modal_shiftCodeList_perType";
import { useNavigate } from "react-router-dom";
import CustomModal from "@/components/modal/CustomModal";
import { useScheduleStore } from "@store/scheduleStore.ts";
import Comp_manpowerCalendar from "../roster_configure/Comp_manpowerCalendar";
import { Check } from "lucide-react";
import Comp_manpowerList from "../roster_configure/Comp_manpowerList";
import { ManpowerViewFilterCheckboxes } from "./ManpowerViewFilterCheckboxes";
import { getDefaultShiftsSortedBySequence } from "@/utils/shift-util";
import useRosterConfig from "@/hooks/useRosterConfig";
import { AiOutlineCalendar } from "react-icons/ai";
import { Input } from "@/components/ui/input";
import httpClient from "@/config/axios";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";
import type { DailyShiftReplaceRequest } from "@/pages/roster-create/service/getDailyShift";

export default function ModalManpower({
  onClose,
  availableShifts,
  defaultShiftCode,
  holidayDates,
  officeId,
  groupId,
  year,
  month,
  initialMonthly,
  initialDaily,
  initialMaxEnabled,
  currentShiftManage,
  totalNursesCount,
}: {
  onClose: () => void;
  availableShifts: type.availableShifts[];
  defaultShiftCode: type.availableShifts[];
  holidayDates: string[];
  officeId: string;
  groupId: string;
  year: number;
  month: number;
  initialMonthly: type.ManPowerMonthly;
  initialDaily: type.ManPowerDaily;
  initialMaxEnabled: boolean;
  currentShiftManage: type.ShiftManageObject[];
  totalNursesCount: number;
}) {
  const { curDate } = useScheduleStore();
  const queryClient = useQueryClient();
  const [isShiftCodeModalOpend, setIsShiftCodeModalOpend] =
    useState<boolean>(false);
  const navigate = useNavigate();
  const [viewType, setViewType] = useState<"calendar" | "list">("list");
  const [filterExceed, setFilterExceed] = useState(true);
  const [filterBelow, setFilterBelow] = useState(true);
  const [confirmType, setConfirmType] = useState<"monthly" | "daily" | null>(
    null
  );
  /** API 전: 월 기본 '최대'는 화면용 로컬 상태(토글 on일 때만 표시) */
  const [showMaxManpower, setShowMaxManpower] = useState(initialMaxEnabled);
  const [monthlyMaxDraft, setMonthlyMaxDraft] = useState<
    Record<string, number>
  >({});
  const [monthlyDraft, setMonthlyDraft] =
    useState<type.ManPowerMonthly>(initialMonthly);
  const [dailyDraft, setDailyDraft] =
    useState<type.ManPowerDaily>(initialDaily);
  const normalizeDailyArray = (arr: unknown, daysInMonth: number) => {
    const source = Array.isArray(arr) ? arr : [];
    return Array.from({ length: daysInMonth }, (_, idx) => {
      const value = source[idx];
      const num =
        typeof value === "string" ? Number(value) : Number(value ?? 0);
      return Number.isFinite(num) ? num : 0;
    });
  };

  const zeroDailyArray = (daysInMonth: number) =>
    Array.from({ length: daysInMonth }, () => 0);

  const getShiftKey = (shift: type.availableShifts) =>
    shift.default_shift || shift.shift_id;

  const getDaysInMonthForDraft = () => {
    const safeYear = curDate?.year ?? 0;
    const safeMonth = curDate?.month ?? 0;
    const fallbackDays =
      safeYear > 0 && safeMonth > 0
        ? new Date(safeYear, safeMonth, 0).getDate()
        : 31;

    return (
      dailyDraft?.D?.length ||
      dailyDraft?.E?.length ||
      dailyDraft?.N?.length ||
      dailyDraft?.M?.length ||
      fallbackDays
    );
  };

  const buildDailyDraftWithMonthlyDefaults = (
    sourceDaily: type.ManPowerDaily
  ) => {
    const daysInMonth = getDaysInMonthForDraft();
    const next = { ...sourceDaily };

    for (const shift of defaultShiftCodeForManpower) {
      const shiftKey = getShiftKey(shift);
      const minValue = Number(
        monthlyDraft?.[shiftKey as keyof type.ManPowerMonthly] ?? 0
      );
      const maxValue = Number(monthlyMaxDraft[shiftKey] ?? minValue);

      next[shiftKey] = Array.from({ length: daysInMonth }, () => minValue);

      if (showMaxManpower) {
        next[`${shiftKey}_max`] = Array.from(
          { length: daysInMonth },
          () => maxValue
        );
      }
    }

    return next;
  };

  const validateMonthlyMinMax = () => {
    if (!showMaxManpower) return true;

    for (const shift of defaultShiftCodeForManpower) {
      const shiftKey = getShiftKey(shift);
      const minValue = Number(
        monthlyDraft?.[shiftKey as keyof type.ManPowerMonthly] ?? 0
      );
      const maxValue = Number(monthlyMaxDraft[shiftKey] ?? minValue);

      if (maxValue > 0 && minValue > maxValue) {
        alert(`${shift.shift_id} 최소 인원은 최대 인원보다 높을 수 없습니다.`);
        return false;
      }
    }

    return true;
  };

  const validateDailyMinMax = (draft: type.ManPowerDaily) => {
    if (!showMaxManpower) return true;

    for (const shift of defaultShiftCodeForManpower) {
      const shiftKey = getShiftKey(shift);
      const minValues = draft[shiftKey] ?? [];
      const maxValues = draft[`${shiftKey}_max`] ?? [];
      const daysInMonth = Math.max(minValues.length, maxValues.length);

      for (let i = 0; i < daysInMonth; i++) {
        const minValue = Number(minValues[i] ?? 0);
        const maxValue = Number(maxValues[i] ?? minValue);
        if (maxValue > 0 && minValue > maxValue) {
          alert(
            `[${i + 1}일] ${shift.shift_id} 최소 인원은 최대 인원보다 높을 수 없습니다.`
          );
          return false;
        }
      }
    }

    return true;
  };

  const applyMonthlyDraftToDailyDraft = () => {
    const nextDaily = buildDailyDraftWithMonthlyDefaults(dailyDraft);
    setDailyDraft(nextDaily);
  };

  const handleUpdateManpowerMonthly = () => {
    if (!validateMonthlyMinMax()) return;

    const totalMonthlyManpower =
      Number(monthlyDraft?.D || 0) +
      Number(monthlyDraft?.E || 0) +
      Number(monthlyDraft?.N || 0) +
      Number(monthlyDraft?.M || 0);

    // 1. 전체 간호사 보다 많으면 안됨!
    if (totalMonthlyManpower > totalNursesCount) {
      alert(`필요인력을 전체 간호사 수보다 많이 지정할 수 없습니다.`);
      return;
    }

    // 2. 경고
    const n = totalNursesCount;
    const threshold = n - (n * 8) / (30 + 3);
    if (totalMonthlyManpower >= threshold) {
      setConfirmType("monthly");
      return;
    }

    applyMonthlyDraftToDailyDraft();
  };

  const handleSaveManpowerDaily = () => {
    if (!validateDailyMinMax(dailyDraft)) return;

    if (dailyDraft.D && dailyDraft.E && dailyDraft.N) {
      const n = totalNursesCount;
      const daysInMonth = dailyDraft.D.length;

      // 1. 각 일 근무 합이 전체근무자 수 보다 많으면 안됨!
      for (let i = 0; i < daysInMonth; i++) {
        const dailySum =
          (dailyDraft.D[i] || 0) +
          (dailyDraft.E[i] || 0) +
          (dailyDraft.N[i] || 0) +
          (dailyDraft.M[i] || 0);
        if (dailySum > totalNursesCount) {
          alert(
            `[${
              i + 1
            }일] D, E, N, M 근무의 합(${dailySum})이 전체 근무자 수(${totalNursesCount})보다 많을 수 없습니다.`
          );
          return;
        }
      }

      // 2. 경고
      const threshold = n * daysInMonth;
      const isExceeded = dailyDraft.D.some((_, i) => {
        const dailySum =
          (dailyDraft.D[i] || 0) +
          (dailyDraft.E[i] || 0) +
          (dailyDraft.N[i] || 0) +
          (dailyDraft.M[i] || 0);
        return dailySum > threshold;
      });

      if (isExceeded) {
        setConfirmType("daily");
        return;
      }
    }
    saveAllManpower(dailyDraft);
  };

  const handleConfirmProceed = () => {
    if (confirmType === "monthly") {
      applyMonthlyDraftToDailyDraft();
    } else if (confirmType === "daily") {
      saveAllManpower(dailyDraft);
    }
    setConfirmType(null);
  };

  const { data: serverConfig } = useRosterConfig();
  const isUseMid = serverConfig?.use_mid;

  const filteredDefaultShiftCode = useMemo(
    () =>
      getDefaultShiftsSortedBySequence(defaultShiftCode, { excludeOff: true }),
    [defaultShiftCode]
  );

  const defaultShiftCodeForManpower = useMemo(
    () =>
      isUseMid !== false
        ? filteredDefaultShiftCode
        : filteredDefaultShiftCode.filter((s) => s.default_shift !== "M"),
    [filteredDefaultShiftCode, isUseMid]
  );

  useEffect(() => {
    setMonthlyDraft(initialMonthly ?? {});
    setMonthlyMaxDraft(() => {
      const next: Record<string, number> = {};
      for (const s of defaultShiftCodeForManpower) {
        const k = getShiftKey(s);
        const maxKey = `${k}_max` as keyof type.ManPowerMonthly;
        const minValue = Number(
          initialMonthly?.[k as keyof type.ManPowerMonthly] ?? 0
        );
        const maxValue = Number(initialMonthly?.[maxKey] ?? minValue);
        next[k] = Number.isFinite(maxValue) ? maxValue : minValue;
      }
      return next;
    });
  }, [defaultShiftCodeForManpower, initialMonthly]);

  useEffect(() => {
    setDailyDraft(initialDaily ?? {});
    setShowMaxManpower(Boolean(initialMaxEnabled));
  }, [initialDaily, initialMaxEnabled]);

  const handleMaxManpowerToggle = (checked: boolean) => {
    setShowMaxManpower(checked);
    if (checked) return;

    setMonthlyMaxDraft((prev) => {
      const next = { ...prev };
      for (const s of defaultShiftCodeForManpower) {
        next[getShiftKey(s)] = 0;
      }
      return next;
    });

    setDailyDraft((prev) => {
      const daysInMonth = getDaysInMonthForDraft();
      const next = { ...prev };
      for (const s of defaultShiftCodeForManpower) {
        next[`${getShiftKey(s)}_max`] = zeroDailyArray(daysInMonth);
      }
      return next;
    });
  };

  const { mutate: saveAllManpower, isPending: isSavingManpower } = useMutation({
    mutationFn: async (draftToSave?: type.ManPowerDaily) => {
      const monthDays = new Date(year, month, 0).getDate();
      const dailyDraftForPayload = draftToSave ?? dailyDraft;
      const dailyPayload: DailyShiftReplaceRequest = {
        office_id: officeId,
        group_id: groupId,
        year,
        month,
        max_enabled: showMaxManpower,
        apply_globally: true,
        date: {
          D_count: normalizeDailyArray(dailyDraftForPayload.D, monthDays),
          E_count: normalizeDailyArray(dailyDraftForPayload.E, monthDays),
          N_count: normalizeDailyArray(dailyDraftForPayload.N, monthDays),
          M_count: normalizeDailyArray(dailyDraftForPayload.M, monthDays),
          D_count_max: showMaxManpower
            ? normalizeDailyArray(dailyDraftForPayload.D_max, monthDays)
            : zeroDailyArray(monthDays),
          E_count_max: showMaxManpower
            ? normalizeDailyArray(dailyDraftForPayload.E_max, monthDays)
            : zeroDailyArray(monthDays),
          N_count_max: showMaxManpower
            ? normalizeDailyArray(dailyDraftForPayload.N_max, monthDays)
            : zeroDailyArray(monthDays),
          M_count_max: showMaxManpower
            ? normalizeDailyArray(dailyDraftForPayload.M_max, monthDays)
            : zeroDailyArray(monthDays),
        },
        month_summary: {
          D_count: Number(monthlyFeedbackBaseline.D ?? 0),
          E_count: Number(monthlyFeedbackBaseline.E ?? 0),
          N_count: Number(monthlyFeedbackBaseline.N ?? 0),
          M_count: Number(monthlyFeedbackBaseline.M ?? 0),
          D_count_max: showMaxManpower
            ? Number(monthlyFeedbackBaseline.D_max ?? 0)
            : 0,
          E_count_max: showMaxManpower
            ? Number(monthlyFeedbackBaseline.E_max ?? 0)
            : 0,
          N_count_max: showMaxManpower
            ? Number(monthlyFeedbackBaseline.N_max ?? 0)
            : 0,
          M_count_max: showMaxManpower
            ? Number(monthlyFeedbackBaseline.M_max ?? 0)
            : 0,
          max_enabled: showMaxManpower,
        },
      };
      await httpClient.put("/daily-shift", dailyPayload);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.shifts.shiftManager(groupId),
      });
      await queryClient.invalidateQueries({
        queryKey: ["manpower", officeId, groupId, year, month],
      });
      onClose();
    },
  });

  useEffect(() => {
    setMonthlyMaxDraft((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const s of defaultShiftCodeForManpower) {
        const k = getShiftKey(s);
        const min = Number(
          monthlyDraft?.[k as keyof type.ManPowerMonthly] ?? 0
        );
        if (next[k] === undefined) {
          next[k] = Math.max(min, 0);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [defaultShiftCodeForManpower, monthlyDraft]);

  const monthlyFeedbackBaseline = useMemo(() => {
    const next = { ...(monthlyDraft ?? {}) };
    for (const s of defaultShiftCodeForManpower) {
      const k = getShiftKey(s);
      const minValue = Number(
        monthlyDraft?.[k as keyof type.ManPowerMonthly] ?? 0
      );
      next[k as keyof type.ManPowerMonthly] = minValue;
      next[`${k}_max` as keyof type.ManPowerMonthly] = Number(
        monthlyMaxDraft?.[k] ?? minValue
      );
    }
    return next;
  }, [defaultShiftCodeForManpower, monthlyDraft, monthlyMaxDraft]);

  return (
    <>
      <CustomModal.Panel
        header
        headerTitle="일자별 근무 인원 설정"
        onClose={onClose}
        isLoading={isSavingManpower}
        loadingMsg="근무인원 설정을 저장하고 있습니다..."
        oneButton
        btnText="저장하기"
        btnSuccessOnClick={handleSaveManpowerDaily}
      >
        <div className="z-[11] flex flex-col  rounded-lg ">
          <div className="rounded-b-lg flex flex-col">
            {/* 안내 영역 */}
            <div className="text-center mb-1">
              <span className="font-pretendard font-bold text-[20px]">
                {curDate?.year}년 {curDate?.month}월
              </span>
            </div>

            {/* 뷰 타입 & 필터 컨트롤 영역 */}
            <div className="flex justify-between items-center py-2 pb-2">
              {/* 왼쪽: 뷰 타입 라디오 (네이티브 input - 모달 드래그 시 흔들림 방지) */}
              <div
                className="flex items-center gap-x-2"
                role="radiogroup"
                aria-label="보기 형태"
              >
                {[
                  { value: "list", label: "나열 형태로 보기" },
                  { value: "calendar", label: "캘린더 형태로 보기" },
                ].map((option) => {
                  const selected = viewType === option.value;
                  return (
                    <label
                      key={option.value}
                      htmlFor={`viewType-${option.value}`}
                      className={cn(
                        "flex cursor-pointer items-center gap-[5px] rounded-[10px] bg-white px-5 py-[11px] font-pretendard text-[14px] transition-colors focus-within:ring-2 focus-within:ring-sig/30 focus-within:ring-offset-1",
                        selected
                          ? "border-2 border-sig font-bold text-sig"
                          : "border border-[#D6D6DE] font-medium text-[#939399]"
                      )}
                    >
                      <input
                        type="radio"
                        name="viewType"
                        id={`viewType-${option.value}`}
                        value={option.value}
                        checked={selected}
                        onChange={() =>
                          setViewType(option.value as "calendar" | "list")
                        }
                        className="sr-only"
                      />
                      {option.value === "list" ? (
                        <RxHamburgerMenu
                          className="size-3 shrink-0"
                          aria-hidden
                        />
                      ) : (
                        <AiOutlineCalendar
                          className="size-[15px] shrink-0"
                          aria-hidden
                        />
                      )}
                      <span>{option.label}</span>
                    </label>
                  );
                })}
              </div>

              {/* 오른쪽: 필터 체크박스 */}
              <ManpowerViewFilterCheckboxes
                filterExceed={filterExceed}
                filterBelow={filterBelow}
                onToggleExceed={() => setFilterExceed((v) => !v)}
                onToggleBelow={() => setFilterBelow((v) => !v)}
              />
            </div>

            {/* 기본 인원 설정 (Figma: 최소/최대 + 최대 인원 설정 토글 + 일괄 적용) */}
            <div className="mb-5 rounded-[10px] border bg-light-muted p-4 relative">
              <div className="flex flex-wrap items-center justify-between absolute right-[4px]   top-[4px] rounded-lg ">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold font-pretendard">
                    최대 인원 설정
                  </span>
                  <Switch
                    checked={showMaxManpower}
                    onCheckedChange={handleMaxManpowerToggle}
                    aria-labelledby="label-max-manpower-toggle"
                  />
                </div>
              </div>
              <div className="flex flex-wrap items-end justify-center ">
                {defaultShiftCodeForManpower?.map((shifts, idx) => {
                  const shiftKey = shifts.default_shift || shifts.shift_id;
                  const minVal =
                    monthlyDraft?.[shiftKey as keyof type.ManPowerMonthly];
                  const maxVal =
                    monthlyMaxDraft[shiftKey] ?? Number(minVal ?? 0);
                  return (
                    <div
                      key={shiftKey}
                      className={cn(
                        "flex items-end px-4 relative gap-2",
                        idx !== defaultShiftCodeForManpower.length - 1 &&
                          "after:pointer-events-none after:absolute after:right-0 after:bottom-0 after:block after:h-[30px] after:w-px after:bg-border after:content-['']"
                      )}
                    >
                      <button
                        type="button"
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[8px] font-pretendard text-[18px] font-bold text-white"
                        style={{ backgroundColor: shifts.color || "#999" }}
                        onClick={() => setIsShiftCodeModalOpend(true)}
                      >
                        {shifts.shift_id}
                      </button>
                      {showMaxManpower && (
                        <div className="flex items-center gap-1">
                          <div className="flex flex-col gap-1">
                            <span className="w-[26px] text-right text-[12px] text-[#666]">
                              최대
                            </span>
                            <Input
                              type="text"
                              inputMode="numeric"
                              className={cn(
                                "h-9 w-12 rounded-md border bg-background text-center text-lg! font-bold text-[#333] outline-none focus:border-sig focus:ring-1 focus:ring-sig/20"
                              )}
                              value={Number(maxVal) === 0 ? "-" : maxVal}
                              onChange={(e) => {
                                const v = e.target.value.replace(/[^0-9]/g, "");
                                const nextMax = v === "" ? 0 : Number(v);
                                setMonthlyMaxDraft((prev) => ({
                                  ...prev,
                                  [shiftKey]: nextMax,
                                }));
                              }}
                              onBlur={() => {
                                const minValue = Number(minVal ?? 0);
                                const currentMax = Number(maxVal ?? 0);
                                if (currentMax > 0 && currentMax < minValue) {
                                  setMonthlyMaxDraft((prev) => ({
                                    ...prev,
                                    [shiftKey]: minValue,
                                  }));
                                }
                              }}
                            />
                          </div>
                        </div>
                      )}
                      {showMaxManpower && (
                        <div className="mb-1 text-muted-foreground">/</div>
                      )}
                      <div className="flex flex-col gap-1 text-center">
                        <span className="text-xs text-muted-foreground">
                          최소
                        </span>
                        <div className="flex items-center gap-1">
                          <Input
                            type="text"
                            inputMode="numeric"
                            className={cn(
                              "h-9 w-12 bg-background rounded-md border text-center text-lg! font-bold  outline-none focus:border-sig focus:ring-1 focus:ring-sig/20"
                            )}
                            value={minVal ?? ""}
                            onChange={(e) => {
                              const v = e.target.value.replace(/[^0-9]/g, "");
                              const nextMin = v === "" ? 0 : Number(v);
                              setMonthlyDraft((prev) => ({
                                ...prev,
                                [shiftKey]: nextMin,
                              }));
                              if (showMaxManpower) {
                                setMonthlyMaxDraft((prev) => {
                                  const prevMax = Number(prev[shiftKey] ?? 0);
                                  if (prevMax === 0 || prevMax >= nextMin) {
                                    return prev;
                                  }
                                  return { ...prev, [shiftKey]: nextMin };
                                });
                              }
                            }}
                            onBlur={(e) => {
                              if (e.currentTarget.value === "") {
                                setMonthlyDraft((prev) => ({
                                  ...prev,
                                  [shiftKey]: 0,
                                }));
                              }
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
                <button
                  type="button"
                  className="flex shrink-0 items-center gap-x-2 rounded-[10px] text-sig! border-sig border p-2 px-3 font-pretendard text-sm font-bold "
                  disabled={isSavingManpower}
                  onClick={handleUpdateManpowerMonthly}
                >
                  일괄 적용
                  <div className="size-5 flex items-center border border-sig justify-center  rounded-full">
                    <Check className="size-3" />
                  </div>
                </button>
              </div>
            </div>

            {/* 캘린더 영역 */}
            <div>
              {curDate &&
                curDate.year &&
                curDate.month &&
                (viewType === "calendar" ? (
                  <Comp_manpowerCalendar
                    activeDate={{ year: curDate.year, month: curDate.month }}
                    defaultShiftCode={defaultShiftCodeForManpower}
                    holidayDates={holidayDates}
                    manPower_daily={dailyDraft}
                    setManpower_daily={setDailyDraft}
                    manPower_monthly={monthlyFeedbackBaseline}
                    filterExceed={filterExceed}
                    filterBelow={filterBelow}
                    showMaxManpower={showMaxManpower}
                  />
                ) : (
                  <Comp_manpowerList
                    activeDate={{ year: curDate.year, month: curDate.month }}
                    defaultShiftCode={defaultShiftCodeForManpower}
                    holidayDates={holidayDates}
                    manPower_daily={dailyDraft}
                    setManpower_daily={setDailyDraft}
                    manPower_monthly={monthlyFeedbackBaseline}
                    filterExceed={filterExceed}
                    filterBelow={filterBelow}
                    showMaxManpower={showMaxManpower}
                  />
                ))}
            </div>
          </div>
        </div>
      </CustomModal.Panel>
      {isShiftCodeModalOpend && (
        <Modal_shiftCodeList_perType
          availableShifts={availableShifts}
          currentShiftManage={currentShiftManage}
          setCurrentTab={() => {
            navigate("/Roster_configure");
          }}
          setIsShiftCodeModalOpend={setIsShiftCodeModalOpend}
        />
      )}
      <CustomModal.Confirm
        isOpen={confirmType !== null}
        onClose={() => setConfirmType(null)}
        onConfirm={handleConfirmProceed}
        confirmText="계속"
        cancelText="취소"
        title={
          confirmType === "daily"
            ? "일자별 필요 인력이 많아 위반사항이 많아질 수 있습니다.\n계속하시겠습니까?"
            : "위반사항이 많아질 수 있습니다. 계속하시겠습니까?"
        }
      />
    </>
  );
}
